import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Plus, Trash2, AlertCircle, CheckCircle2, Wrench,
  Package, Search, X,
} from 'lucide-react';
import { api } from '@/utils/api';

// Component Issue & Return page — two modes in one screen:
// 1. Issue components to the ticket (machine_tool or spare_part)
// 2. Reconcile on closure: enter qtyReturned + qtyConsumed, validate invariants.
//
// Norman's rule: invariants are shown INLINE next to the offending field,
// not buried in a toast. Submit button is disabled with a contrasting
// "why" message until the form is valid.

type ComponentType = 'machine_tool' | 'spare_part';

interface ProductMini {
  id: string;
  name: string;
  sku?: string;
  unitOfMeasure?: string;
}

interface ComponentIssue {
  id: string;
  ticketId: string;
  componentType: ComponentType;
  productId: string;
  product?: ProductMini;
  qtyIssued: string | number;
  qtyReturned: string | number;
  qtyConsumed: string | number;
  issuedAt?: string;
  returnedAt?: string | null;
  notes?: string | null;
}

interface ReconciliationResponse {
  rows: Array<ComponentIssue & { isValid: boolean; error?: string }>;
  isTicketReady: boolean;
}

export function ComponentIssuePage() {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ComponentIssue[]>([]);
  const [products, setProducts] = useState<ProductMini[]>([]);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({
    componentType: 'spare_part' as ComponentType,
    productId: '',
    qtyIssued: '1',
    notes: '',
  });
  const [productSearch, setProductSearch] = useState('');

  // Edits to qtyReturned / qtyConsumed per row
  const [edits, setEdits] = useState<Record<string, { qtyReturned: string; qtyConsumed: string }>>({});
  const [savingRow, setSavingRow] = useState<string | null>(null);

  const fetchIssues = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/service/tickets/${ticketId}/components`);
      const data = res.data.data?.issues ?? res.data.data ?? [];
      setIssues(Array.isArray(data) ? data : []);
    } catch {
      // Fall back to reconciliation endpoint which may include the rows
      try {
        const rec = await api.get(`/service/tickets/${ticketId}/components/reconciliation`);
        const rows = rec.data.data?.rows ?? rec.data.rows ?? [];
        setIssues(Array.isArray(rows) ? rows : []);
      } catch (err) {
        const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
          || 'Failed to load components';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchIssues();
    api.get('/products?limit=200').then(r => {
      const data = r.data.data?.products ?? r.data.data ?? [];
      setProducts(Array.isArray(data) ? data : []);
    }).catch(() => setProducts([]));
  }, [fetchIssues]);

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products.slice(0, 20);
    const q = productSearch.toLowerCase();
    return products
      .filter(p => (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [products, productSearch]);

  const validateRow = (row: ComponentIssue): { ok: boolean; msg: string } => {
    const edit = edits[row.id];
    const issued = Number(row.qtyIssued) || 0;
    const returned = edit ? Number(edit.qtyReturned) : Number(row.qtyReturned) || 0;
    const consumed = edit ? Number(edit.qtyConsumed) : Number(row.qtyConsumed) || 0;

    if (row.componentType === 'machine_tool') {
      if (consumed !== 0) return { ok: false, msg: 'Machine tools cannot be consumed' };
      if (returned !== issued) return { ok: false, msg: `Must return all ${issued} — machine tool` };
    } else {
      if (Math.abs(returned + consumed - issued) > 0.0001) {
        return { ok: false, msg: `Returned + Consumed must equal Issued (${issued})` };
      }
    }
    return { ok: true, msg: 'Balanced' };
  };

  const allReconciled = useMemo(
    () => issues.length > 0 && issues.every(r => validateRow(r).ok),
    [issues, edits], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleAdd = async () => {
    if (!ticketId || !addForm.productId || Number(addForm.qtyIssued) <= 0) return;
    setAdding(true);
    try {
      await api.post(`/service/tickets/${ticketId}/components`, {
        componentType: addForm.componentType,
        productId: addForm.productId,
        qtyIssued: Number(addForm.qtyIssued),
        notes: addForm.notes || undefined,
      });
      setShowAdd(false);
      setAddForm({ componentType: 'spare_part', productId: '', qtyIssued: '1', notes: '' });
      setProductSearch('');
      fetchIssues();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to add component';
      alert(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveRow = async (row: ComponentIssue) => {
    const edit = edits[row.id];
    if (!edit) return;
    const v = validateRow(row);
    if (!v.ok) {
      alert(v.msg);
      return;
    }
    setSavingRow(row.id);
    try {
      await api.patch(`/service/components/${row.id}`, {
        qtyReturned: Number(edit.qtyReturned),
        qtyConsumed: Number(edit.qtyConsumed),
      });
      setEdits(prev => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      fetchIssues();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to save';
      alert(msg);
    } finally {
      setSavingRow(null);
    }
  };

  const handleDelete = async (row: ComponentIssue) => {
    if (!confirm(`Remove ${row.product?.name || 'component'}?`)) return;
    try {
      await api.delete(`/service/components/${row.id}`);
      fetchIssues();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to remove';
      alert(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${ticketId}`)}
            className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Components</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Issue on dispatch, reconcile on closure.</p>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} />
          Issue Component
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Reconciliation status */}
      <div
        className={`mb-4 flex items-center gap-2 rounded-lg border p-3 text-sm ${
          issues.length === 0
            ? 'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
            : allReconciled
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
              : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
        }`}
      >
        {issues.length === 0 ? (
          <>
            <Package size={16} />
            No components issued yet.
          </>
        ) : allReconciled ? (
          <>
            <CheckCircle2 size={16} />
            All components reconciled. Ticket can be finished.
          </>
        ) : (
          <>
            <AlertCircle size={16} />
            Reconcile all rows before finishing the ticket.
          </>
        )}
      </div>

      {/* Grouped sections */}
      <div className="space-y-6">
        <ComponentSection
          title="Machine Tools"
          subtitle="Must be returned in full. No consumption allowed."
          items={issues.filter(i => i.componentType === 'machine_tool')}
          edits={edits}
          setEdits={setEdits}
          validate={validateRow}
          onSave={handleSaveRow}
          onDelete={handleDelete}
          savingRow={savingRow}
        />
        <ComponentSection
          title="Spare Parts"
          subtitle="Returned + Consumed must equal Issued."
          items={issues.filter(i => i.componentType === 'spare_part')}
          edits={edits}
          setEdits={setEdits}
          validate={validateRow}
          onSave={handleSaveRow}
          onDelete={handleDelete}
          savingRow={savingRow}
        />
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAdd(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-gray-900"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Issue Component</h2>
              <button
                onClick={() => setShowAdd(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Type *</label>
                <div className="flex gap-2">
                  {(['machine_tool', 'spare_part'] as const).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAddForm(f => ({ ...f, componentType: t }))}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        addForm.componentType === t
                          ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)] dark:text-[var(--saffron-600)]'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {t === 'machine_tool' ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <Wrench size={14} /> Machine tool
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-1.5">
                          <Package size={14} /> Spare part
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Product *</label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    placeholder="Search SKU or name"
                    className="w-full rounded-lg border border-gray-300 bg-white px-8 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
                  {filteredProducts.map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setAddForm(f => ({ ...f, productId: p.id }));
                          setProductSearch(p.name);
                        }}
                        className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                          addForm.productId === p.id
                            ? 'bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)] dark:text-[var(--saffron-600)]'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <span>{p.name}</span>
                        <span className="text-xs text-gray-500">{p.sku}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Quantity issued *
                </label>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={addForm.qtyIssued}
                  onChange={e => setAddForm(f => ({ ...f, qtyIssued: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <input
                  type="text"
                  value={addForm.notes}
                  onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setShowAdd(false)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={adding || !addForm.productId || Number(addForm.qtyIssued) <= 0}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {adding && <Loader2 size={14} className="animate-spin" />}
                Issue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────── Inline table section ─────────

function ComponentSection({
  title,
  subtitle,
  items,
  edits,
  setEdits,
  validate,
  onSave,
  onDelete,
  savingRow,
}: {
  title: string;
  subtitle: string;
  items: ComponentIssue[];
  edits: Record<string, { qtyReturned: string; qtyConsumed: string }>;
  setEdits: React.Dispatch<React.SetStateAction<Record<string, { qtyReturned: string; qtyConsumed: string }>>>;
  validate: (row: ComponentIssue) => { ok: boolean; msg: string };
  onSave: (row: ComponentIssue) => void;
  onDelete: (row: ComponentIssue) => void;
  savingRow: string | null;
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
        <div className="mb-1 font-semibold text-gray-700 dark:text-gray-300">{title}</div>
        <div className="text-xs">{subtitle}</div>
        <div className="mt-2 text-xs text-gray-400">None issued</div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            <th className="px-3 py-2">Product</th>
            <th className="px-3 py-2 text-right">Issued</th>
            <th className="px-3 py-2 text-right">Returned</th>
            <th className="px-3 py-2 text-right">Consumed</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(row => {
            const edit = edits[row.id];
            const v = validate(row);
            const isEdited = !!edit;
            return (
              <tr key={row.id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                  <div className="font-medium">{row.product?.name || 'Unknown'}</div>
                  {row.product?.sku && <div className="text-xs text-gray-500">{row.product.sku}</div>}
                </td>
                <td className="px-3 py-2 text-right font-mono text-gray-700 dark:text-gray-300">
                  {String(row.qtyIssued)}
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={edit?.qtyReturned ?? String(row.qtyReturned)}
                    onChange={e => setEdits(prev => ({
                      ...prev,
                      [row.id]: {
                        qtyReturned: e.target.value,
                        qtyConsumed: prev[row.id]?.qtyConsumed ?? String(row.qtyConsumed),
                      },
                    }))}
                    className={`w-20 rounded border px-2 py-1 text-right text-sm focus:outline-none ${
                      v.ok
                        ? 'border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
                        : 'border-red-400 bg-red-50 dark:bg-red-950'
                    }`}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    disabled={row.componentType === 'machine_tool'}
                    value={edit?.qtyConsumed ?? String(row.qtyConsumed)}
                    onChange={e => setEdits(prev => ({
                      ...prev,
                      [row.id]: {
                        qtyReturned: prev[row.id]?.qtyReturned ?? String(row.qtyReturned),
                        qtyConsumed: e.target.value,
                      },
                    }))}
                    className={`w-20 rounded border px-2 py-1 text-right text-sm focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-gray-800 ${
                      v.ok
                        ? 'border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
                        : 'border-red-400 bg-red-50 dark:bg-red-950'
                    }`}
                  />
                </td>
                <td className="px-3 py-2">
                  {v.ok ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-400">
                      <CheckCircle2 size={12} /> {v.msg}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-400">
                      <AlertCircle size={12} /> {v.msg}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isEdited && (
                      <button
                        onClick={() => onSave(row)}
                        disabled={!v.ok || savingRow === row.id}
                        className="rounded-md bg-[var(--ink-0)] px-2 py-1 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        {savingRow === row.id ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(row)}
                      className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                      aria-label="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
